import { create } from 'zustand';

/**
 * Cross-mount selection state for the Metro Detail page (IMP-006).
 *
 * Maria, 2026-06-01 Slack 15:08 (meeting recap):
 *   "Update the last 3 tabs so that they retain the data when a user
 *    clicks away and comes back to it."
 *
 * The Map page already had this property because its selected-metro
 * state lives in `useMetroStore` (an in-memory zustand singleton).
 * FYI-001 confirmed by Zack -- preserve that behavior. We mirror the
 * Map pattern here rather than reach for URL-sync or zustand persist:
 *
 *   - URL params are already used as a one-shot SEED via
 *     `useUrlParamSeed` (DEC-061). Re-purposing them as a continuous
 *     sync would break the one-shot semantics + their seven explicit
 *     tests in use-url-param-seed.test.tsx.
 *   - `zustand/middleware/persist` would pull selections into
 *     localStorage, leaking across browser sessions (not what Maria
 *     asked for) and introducing a storage layer Map doesn't have.
 *
 * Keeping the store ultra-narrow: only the two fields the Detail
 * page picker controls. Page-local UI state (open dropdown, hover)
 * remains in component state.
 */
interface DetailSelectionStore {
  readonly metroAreaId: string;
  readonly occupationalGroupId: string;
  setMetroAreaId: (id: string) => void;
  setOccupationalGroupId: (id: string) => void;
  reset: () => void;
}

export const useDetailSelectionStore = create<DetailSelectionStore>((set) => ({
  metroAreaId: '',
  occupationalGroupId: '',
  setMetroAreaId: (id) => set({ metroAreaId: id }),
  setOccupationalGroupId: (id) => set({ occupationalGroupId: id }),
  reset: () => set({ metroAreaId: '', occupationalGroupId: '' }),
}));
